import React, { useEffect, useState } from "react";
import { Dialog, Loading, GridGenerator } from "@actinc/dls/components";
import { useForm, useFormState } from "react-hook-form";
import { useClient, useMutation, maskTypename } from "urql";
import { useStore } from "../../../../services/hooks";
import { searchItemsByMetadataQuery } from "../../../../routes/Search/Items";
import { searchPassagesByMetadataQuery } from "../../../../routes/Search/Passages";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import {
  ControlledFormSelect,
  ControlledFormInput,
  DataGrid,
} from "../../../../components";
import { updateProgramMutation } from "./";

const SearchDialog = ({
  dialogTitle,
  maxWidth = "lg",
  metafileIndex,
  open,
  program,
  programID,
  onClose,
  src,
}) => {
  const client = useClient();
  const [, mutateProgram] = useMutation(updateProgramMutation);
  const [checkedGridRows, setCheckedGridRows] = useState([]);
  const handleCancel = () => {
    searchStore.set({});
    setCheckedGridRows([]);
    onClose(false);
  };

  const [search, searchStore] = useStore("search");
  const handleReset = () => {
    reset({ ...defaultValues });
    searchStore.set({});
    setEnableSearch(false);
    setCheckedGridRows([]);
  };

  const handleSearch = async () => {
    const searchValues = getValues();
    setLoading(true);
    if (searchValues.type === "item") {
      await searchStore.fetch("item")(searchItemsByMetadataQuery)({
        ...searchValues,
        name: searchValues?.name.trim(),
        program: program.id,
        type: null,
      });
    } else if (searchValues.type === "passage") {
      await searchStore.fetch("passage")(searchPassagesByMetadataQuery)({
        ...searchValues,
        name: searchValues?.name.trim(),
        program: program.id,
        type: null,
      });
    }
    setLoading(false);
  };

  const handleSelectionModelChange = (data) => (selection) => {
    const selectedRows = data.filter(({ id }) => selection.includes(id));
    setCheckedGridRows(selectedRows);
  };

  const handleAssociate = async () => {
    setLoading(true);
    const { id, _actions, __typename, ...programInput } = program;
    let associations;
    if (getValues().type === "passage") {
      associations = programInput.metafileAssets[metafileIndex]
        .passageAssociations
        ? programInput.metafileAssets[metafileIndex].passageAssociations
        : [];
    } else {
      associations = programInput.metafileAssets[metafileIndex].itemAssociations
        ? programInput.metafileAssets[metafileIndex].itemAssociations
        : [];
    }
    checkedGridRows.forEach((element) => {
      if (!associations.includes(element.id)) associations.push(element.id);
    });
    const metafileAssets = program.metafileAssets.map(
      ({ __typename, ...keepAttrs }) => keepAttrs,
    );
    if (getValues().type === "passage") {
      metafileAssets[metafileIndex].passageAssociations = associations;
    } else {
      metafileAssets[metafileIndex].itemAssociations = associations;
    }
    programInput.metafileAssets = metafileAssets;
    await mutateProgram({
      data: maskTypename({
        ...programInput,
        metafileAssets,
      }),
      id: programID,
    });
    program = programInput;
    setLoading(false);
    searchStore.set({});
    setCheckedGridRows([]);
    onClose(true);
  };

  const handleTypeChange = () => {
    searchStore.set({});
    setEnableSearch(true);
  };

  const defaultValues = {
    type: null,
    name: "",
    rowLimit: 100,
  };

  const [enableSearch, setEnableSearch] = useState(false);
  const [loading, setLoading] = useState(false);

  const { control, getValues, handleSubmit, reset } = useForm({
    defaultValues: { ...defaultValues },
    mode: "all",
    revalidateMode: "onBlur",
  });

  const columns = [
    {
      field: "name",
      flex: 1,
      headerName: "Name",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
      type: "string",
    },
    {
      field: "contentArea",
      flex: 1,
      headerName: "Content Area",
      type: "string",
    },
    {
      field: "developmentState",
      flex: 1,
      headerName: "Development State",
      type: "string",
    },
  ];

  const rows =
    search?.response?.data?.itemsByMetadata ||
    search?.response?.data?.passagesByMetadata ||
    [];

  const dataGridProps = {
    checkboxSelection: true,
    columns,
    error: search?.response?.error?.message,
    loading,
    onRowSelectionModelChange: handleSelectionModelChange(rows),
    rows,
  };

  const typeOptions = ["Item", "Passage"].map((value) => ({
    label: value,
    value: value.toLowerCase(),
  }));

  const rowLimitOptions = [100, 500, 1000].map((value) => ({
    label: value,
    value,
  }));

  return (
    <>
      <Dialog maxWidth={maxWidth} open={true} onClose={handleCancel}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Paper sx={{ display: "flex" }}>
            <Typography sx={{ marginRight: 2 }} variant="subtitle1">
              Program: {program?.name}
            </Typography>
          </Paper>
          <Box
            sx={{
              alignItems: "end",
              display: "flex",
              flexDirection: "column",
              marginTop: 3,
            }}
          >
            <GridGenerator
              groups={[
                [
                  {
                    children: (
                      <ControlledFormSelect
                        control={control}
                        fullWidth={true}
                        id={`genericSearchType`}
                        label="Search Type"
                        name="type"
                        onChange={handleTypeChange}
                        options={typeOptions}
                        rules={{ required: true }}
                      />
                    ),
                    key: `genericSearch-type`,
                  },
                  {
                    children: (
                      <ControlledFormInput
                        control={control}
                        fullWidth={true}
                        id={`genericSearchName`}
                        label="Search Name"
                        name="name"
                      />
                    ),
                    key: `genericSearch-name`,
                  },
                  {
                    children: (
                      <ControlledFormSelect
                        control={control}
                        fullWidth={true}
                        id={`genericSearchRowLimit`}
                        label="Number of Results"
                        name="rowLimit"
                        options={rowLimitOptions}
                      />
                    ),
                    key: `genericSearch-rowlimit`,
                  },
                ],
              ]}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
            justifyContent="flex-end"
          >
            <Button
              onClick={handleReset}
              color="primary"
              variant="outlined"
              sx={{
                marginRight: 2,
              }}
            >
              Reset
            </Button>
            <Button
              onClick={handleSearch}
              color="primary"
              variant="contained"
              disabled={!enableSearch}
              sx={{
                marginRight: 2,
              }}
            >
              Search
            </Button>
          </Box>
          <Paper
            elevation={1}
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              height: "500px",
              margin: 1,
              minHeight: "500px",
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minHeight: "500px",
              }}
            >
              <DataGrid {...dataGridProps}></DataGrid>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleAssociate}
            color="primary"
            variant="contained"
            disabled={!checkedGridRows.length}
          >
            Associate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { SearchDialog };
